<template>
  <v-container fluid class="pa-4">
    <v-card class="elevation-2">
      <v-toolbar color="blue-grey darken-2" dark dense>
        <v-icon left>tune</v-icon>
        <v-toolbar-title>Envanter Tanımları</v-toolbar-title>
        <v-spacer />
        <v-btn icon :loading="loading" @click="load">
          <v-icon>refresh</v-icon>
        </v-btn>
        <v-btn color="white" flat class="inv-btn ma-0" @click="openCreate">
          <v-icon left small>add</v-icon>
          Yeni Tanım
        </v-btn>
      </v-toolbar>

      <v-card-text>
        <div class="inv-hint">
          Envantere kalem eklerken çıkan açılır listeleri buradan yönetin. Takı için ürün/ölçü/ayar, Külçe için ürün/ayar,
          Ziynet için ürün tanımlanır. Pasif tanımlar ekleme ekranlarında listelenmez.
        </div>

        <v-layout wrap align-center class="mb-3">
          <v-flex xs12 sm3 class="pr-2">
            <v-select
              v-model="selectedType"
              :items="typeItems"
              item-text="label"
              item-value="value"
              label="Tip"
              prepend-icon="category"
              hide-details
              @change="onTypeChange"
            ></v-select>
          </v-flex>
          <v-flex xs12 sm9>
            <v-btn-toggle v-model="selectedCategory" mandatory class="elevation-0">
              <v-btn
                v-for="c in availableCategories"
                :key="'cat-' + c.value"
                :value="c.value"
                flat
              >{{ c.label }}</v-btn>
            </v-btn-toggle>
          </v-flex>
        </v-layout>

        <v-layout wrap class="inv-stats">
          <v-flex xs4 class="pr-2">
            <div class="inv-stat inv-stat--teal">
              <div class="inv-stat-label">Tanım</div>
              <div class="inv-stat-value">{{ items.length }}</div>
            </div>
          </v-flex>
          <v-flex xs4 class="pr-2">
            <div class="inv-stat inv-stat--green">
              <div class="inv-stat-label">Aktif</div>
              <div class="inv-stat-value">{{ activeCount }}</div>
            </div>
          </v-flex>
          <v-flex xs4>
            <div class="inv-stat inv-stat--grey">
              <div class="inv-stat-label">Pasif</div>
              <div class="inv-stat-value">{{ items.length - activeCount }}</div>
            </div>
          </v-flex>
        </v-layout>

        <div v-if="!items.length && !loading" class="inv-empty">
          <v-icon large color="grey lighten-1">list</v-icon>
          <div>Bu liste için henüz tanım yok.</div>
        </div>

        <div v-else>
          <div class="inv-card" v-for="item in items" :key="item.id" :class="item.isActive ? '' : 'inv-card--grey'">
            <div class="inv-icon" :class="item.isActive ? typeIconClass : 'inv-icon--grey'">
              <v-icon color="white" small>{{ typeIcon }}</v-icon>
            </div>

            <div class="inv-card-l">
              <div class="inv-head">
                <span class="inv-title">{{ item.label }}</span>
                <span class="inv-chip" :class="item.isActive ? 'inv-chip--done' : 'inv-chip--grey'">
                  {{ item.isActive ? 'Aktif' : 'Pasif' }}
                </span>
                <span v-if="isZiynetProduct && item.purity != null" class="inv-chip inv-chip--blue ml-2">
                  {{ formatNum(item.purity) }}<template v-if="item.purityLabel"> · {{ item.purityLabel }}</template>
                </span>
              </div>

              <div class="inv-meta">
                <v-icon x-small color="blue-grey lighten-2">sort</v-icon>
                Sıra: {{ item.sortOrder || 0 }}
                <span class="inv-dot">·</span>
                {{ currentTypeLabel }} / {{ currentCategoryLabel }}
              </div>
            </div>

            <div class="inv-card-r">
              <div class="inv-value">
                <div class="inv-value-num">{{ item.value != null ? formatNum(item.value) : '—' }}</div>
                <div class="inv-value-label">{{ isAyar ? 'milyem' : 'değer' }}</div>
              </div>
              <v-btn icon small class="inv-btn ma-0 ml-3" @click="openEdit(item)">
                <v-icon small color="blue">edit</v-icon>
              </v-btn>
              <v-btn icon small class="inv-btn ma-0" @click="remove(item)">
                <v-icon small color="red">delete</v-icon>
              </v-btn>
            </div>
          </div>
        </div>
      </v-card-text>
    </v-card>

    <v-dialog v-model="dialog" max-width="460" persistent>
      <v-card>
        <v-card-title class="blue-grey darken-2 white--text">
          <v-icon left color="white">{{ editing ? 'edit' : 'add' }}</v-icon>
          {{ editing ? 'Tanım Düzenle' : 'Yeni Tanım' }} — {{ currentTypeLabel }} / {{ currentCategoryLabel }}
        </v-card-title>
        <v-card-text class="pa-4">
          <v-text-field v-model="form.label" :label="labelFieldLabel" :placeholder="labelPlaceholder" autofocus></v-text-field>
          <v-text-field
            v-if="showValue"
            v-model="form.value"
            :label="valueFieldLabel"
            :placeholder="valuePlaceholder"
          ></v-text-field>
          <v-text-field
            v-if="isZiynetProduct"
            v-model="form.purity"
            label="Ayar / Milyem (ör. 916,6)"
            placeholder="916,6"
          ></v-text-field>
          <v-text-field
            v-if="isZiynetProduct"
            v-model="form.purityLabel"
            label="Ayar Etiketi (ör. 22 Ayar)"
            placeholder="22 Ayar"
          ></v-text-field>
          <v-text-field v-model.number="form.sortOrder" label="Sıra" type="number"></v-text-field>
          <v-switch v-model="form.isActive" :label="form.isActive ? 'Aktif' : 'Pasif'" color="green"></v-switch>
        </v-card-text>
        <v-card-actions class="pa-3">
          <v-spacer></v-spacer>
          <v-btn flat color="grey" @click="dialog = false">İptal</v-btn>
          <v-btn color="blue-grey darken-2" dark class="inv-btn" :loading="saving" @click="save">Kaydet</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
// Sabitler backend ile aynı: InventoryType 0=Takı,1=Ziynet,2=Külçe; Category 0=Ürün,1=Ölçü,2=Ayar
const CAT = { Product: 0, Size: 1, Ayar: 2 };

export default {
  name: "InventoryDefinitions",
  data: () => ({
    loading: false,
    saving: false,
    items: [],
    selectedType: 0,
    selectedCategory: 0,
    dialog: false,
    editing: false,
    form: { id: null, label: "", value: "", purity: "", purityLabel: "", sortOrder: 0, isActive: true },
    typeItems: [
      { value: 0, label: "Takı" },
      { value: 2, label: "Külçe" },
      { value: 1, label: "Ziynet" },
    ],
    categoriesByType: {
      0: [
        { value: CAT.Product, label: "Ürün" },
        { value: CAT.Size, label: "Ölçü" },
        { value: CAT.Ayar, label: "Ayar" },
      ],
      2: [
        { value: CAT.Product, label: "Ürün" },
        { value: CAT.Ayar, label: "Ayar" },
      ],
      1: [{ value: CAT.Product, label: "Ürün" }],
    },
  }),

  computed: {
    availableCategories() {
      return this.categoriesByType[this.selectedType] || [];
    },
    currentTypeLabel() {
      const t = this.typeItems.find((x) => x.value === this.selectedType);
      return t ? t.label : "";
    },
    currentCategoryLabel() {
      const c = this.availableCategories.find((x) => x.value === this.selectedCategory);
      return c ? c.label : "";
    },
    isAyar() {
      return this.selectedCategory === CAT.Ayar;
    },
    isZiynetProduct() {
      return this.selectedType === 1 && this.selectedCategory === CAT.Product;
    },
    isKulceProduct() {
      return this.selectedType === 2 && this.selectedCategory === CAT.Product;
    },
    showValue() {
      return this.isAyar || this.isKulceProduct || this.isZiynetProduct;
    },
    labelFieldLabel() {
      if (this.selectedCategory === CAT.Size) return "Ölçü";
      if (this.isAyar) return "Ayar Adı";
      return "Ürün Adı";
    },
    labelPlaceholder() {
      if (this.selectedCategory === CAT.Size) return "Örn. 7.2";
      if (this.isAyar) return "Örn. 22 Ayar";
      if (this.isKulceProduct) return "Örn. 1 gr";
      if (this.isZiynetProduct) return "Örn. Çeyrek";
      return "Örn. Bilezik";
    },
    valueFieldLabel() {
      if (this.isAyar) return "Milyem (ör. 916,6)";
      return "Gram (ör. 1,75)";
    },
    valuePlaceholder() {
      return this.isAyar ? "916,6" : "1,75";
    },
    activeCount() {
      return this.items.filter((i) => i.isActive).length;
    },
    typeIcon() {
      if (this.selectedType === 1) return "monetization_on";
      if (this.selectedType === 2) return "view_in_ar";
      return "grade";
    },
    typeIconClass() {
      if (this.selectedType === 1) return "inv-icon--amber";
      if (this.selectedType === 2) return "inv-icon--brown";
      return "inv-icon--purple";
    },
  },

  watch: {
    selectedCategory() {
      this.load();
    },
  },

  created() {
    this.load();
  },

  methods: {
    parseNum(s) {
      if (s == null || s === "") return null;
      const n = parseFloat(String(s).trim().replace(",", "."));
      return Number.isNaN(n) ? null : n;
    },
    formatNum(v) {
      if (v == null) return "—";
      const n = Number(v);
      if (Number.isNaN(n)) return String(v);
      return n.toLocaleString("tr-TR", { maximumFractionDigits: 4 });
    },
    trStr(n) {
      return n == null ? "" : String(n).replace(".", ",");
    },
    onTypeChange() {
      // Tip değişince mevcut kategori geçersizse ilkine düş (watch -> load tetikler).
      // Geçerliyse kategori değişmez; load'u elle çağırırız.
      if (!this.availableCategories.some((c) => c.value === this.selectedCategory)) {
        this.selectedCategory = this.availableCategories[0].value;
      } else {
        this.load();
      }
    },
    async load() {
      this.loading = true;
      try {
        this.items =
          (await this.$store.dispatch("inventory/getDefinitions", {
            inventoryType: this.selectedType,
            category: this.selectedCategory,
          })) || [];
      } catch (e) {
        this.notify("Tanımlar yüklenemedi: " + this.errMsg(e));
      } finally {
        this.loading = false;
      }
    },
    openCreate() {
      this.editing = false;
      const nextSort = this.items.length ? Math.max(...this.items.map((i) => i.sortOrder || 0)) + 1 : 1;
      this.form = { id: null, label: "", value: "", purity: "", purityLabel: "", sortOrder: nextSort, isActive: true };
      this.dialog = true;
    },
    openEdit(item) {
      this.editing = true;
      this.form = {
        id: item.id,
        label: item.label,
        value: this.trStr(item.value),
        purity: this.trStr(item.purity),
        purityLabel: item.purityLabel || "",
        sortOrder: item.sortOrder || 0,
        isActive: item.isActive,
      };
      this.dialog = true;
    },
    buildDto() {
      return {
        inventoryType: this.selectedType,
        category: this.selectedCategory,
        label: (this.form.label || "").trim(),
        value: this.showValue ? this.parseNum(this.form.value) : null,
        purity: this.isZiynetProduct ? this.parseNum(this.form.purity) : null,
        purityLabel: this.isZiynetProduct ? (this.form.purityLabel || "").trim() : null,
        sortOrder: Number(this.form.sortOrder) || 0,
        isActive: this.form.isActive,
      };
    },
    async save() {
      if (!this.form.label || !this.form.label.trim()) {
        this.notify("Ad/etiket girin.");
        return;
      }
      this.saving = true;
      try {
        const dto = this.buildDto();
        if (this.editing) {
          await this.$store.dispatch("inventory/updateDefinition", { id: this.form.id, dto });
        } else {
          await this.$store.dispatch("inventory/createDefinition", dto);
        }
        this.dialog = false;
        await this.load();
      } catch (e) {
        this.notify("Hata: " + this.errMsg(e));
      } finally {
        this.saving = false;
      }
    },
    async remove(item) {
      if (!confirm(`'${item.label}' silinsin mi?`)) return;
      try {
        await this.$store.dispatch("inventory/deleteDefinition", item.id);
        await this.load();
      } catch (e) {
        this.notify("Hata: " + this.errMsg(e));
      }
    },
    errMsg(e) {
      return (e && e.response && e.response.data && e.response.data.message) || (e && e.message) || "Bilinmeyen hata";
    },
    notify(msg) {
      this.$store.state.dialog = true;
      this.$store.state.message = msg;
    },
  },
};
</script>
